// material-table 8 has a cycle in its own module graph:
//
//   defaults/props.components.js -> MTableBodyRow -> store/LocalizationStore
//                                -> defaults/props.components.js
//
// `props.components.js` builds its object AT MODULE LOAD and needs
// MTableBodyRow's default export ready; MTableBodyRow needs the store, and the
// store needs the defaults - but only when a store is CREATED, which is
// runtime. So entering through the store works and entering through
// MTableBodyRow does not: in the second order `Row` is baked in as `undefined`,
// the body renders it, and every screen dies with "Element type is invalid".
//
// The store cannot be imported first to pin the order: the package's `exports`
// only allow its root. The patch in app/patches turns every default into a
// getter instead, read when a store is created. EVERY one: `Toolbar` was left
// out at first, and a module-level reference to MTableToolbar in this file was
// then enough to reorder the cycle and blank it - every table failed to draw.
import MaterialTable, { MTableBodyRow, MTableToolbar } from '@material-table/core';
import { Paper, TablePagination } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { withStyles } from 'utils/styles';
import Typography from '@mui/material/Typography';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { css } from '@emotion/css';
import { sortBy } from 'lodash';
import PropTypes from 'prop-types';
import React, { useMemo, useRef, useState } from 'react';
import theme from 'source/theme';
import tableIcons from 'utils/icons/materialTableIcons';
import { orderKeys } from 'utils/tables/order';
import { TABLE_HEIGHT, TABLE_HEIGHT_SPLIT } from 'utils/ui/constants';

/*
 * No column in this application declares `defaultSort`, so material-table never
 * imposes an order of its own: the order of the array IS the order on screen.
 * Which means that until now every table showed its rows in whatever order they
 * were built in - a payload's, a `forEach`'s, an index's - and nobody chose it.
 *
 * `id` says nothing to a reader, and that is fine. What it has is the property
 * that matters: it is the same on every render, in every process, on both sides
 * of a comparison.
 *
 * `order={false}` for a table that orders itself. Twenty-five do - the selector
 * tables float their checked rows to the top, the consumptions go by year - and
 * sorting them here would undo it.
 */
const DEFAULT_ORDER = ['id'];

const styles = {
  pagination: {
    backgroundColor: theme.palette.primary.light,
    // MUI 4 left this toolbar with no padding of its own; MUI 9 gives it 24 on
    // the left and 2 on the right, and the 2 push the page buttons that far in
    // from the right edge of every table.
    '& .MuiTablePagination-toolbar': {
      padding: 0
    }
  },
  rowStyleNotClickable: {
    "&:hover": {
      cursor: "default !important"
    }
  },
};

const Component = React.forwardRef((props, ref) => {
  const {
    actions, cellEditable, classes, columns, components, data, detailPanel, editable, halfSize, isLoading, localization,
    onDoubleClickRowEdit, onRowDoubleClick, onRowClick, onSelectionChange, onSearchChange, options, order,
    paging = true, title, titleAction, titleBack
  } = props;

  const [stateForcedRefresh, setStateForcedRefresh] = useState(true);

  const orderedData = order === false || !Array.isArray(data) ?
    data :
    sortBy(data, orderKeys(order || DEFAULT_ORDER));

  // material-table 8 merges its own defaults with object spread:
  //
  //     const props = { ...defaultProps, ...userProps }
  //
  // React's `defaultProps` filled in for a prop that was absent OR undefined.
  // A spread only fills in for absent - `actions={undefined}` overwrites the
  // default `[]` with nothing, and the first thing the constructor does is
  // `actions.filter(...)`. Every screen threw before drawing a row.
  //
  // So: whatever we do not have, we do not pass. These are the props the
  // library declares a default for and this component forwards; the rest have
  // no default and undefined is the right answer for them.
  // `classes` is NOT in here: it is this component's own styles, it was never
  // passed to the library before, and passing it now would be a change this
  // branch does not get to make.
  const declared = { actions, isLoading };
  const forwarded = Object.fromEntries(
    Object.entries(declared).filter(([, value]) => value !== undefined)
  );

  const enhancedTitle = () => {
    if (titleBack) {
      return (
        <React.Fragment>
          <Typography variant='h6'>
            <IconButton
              color='secondary'
              aria-label='go back'
              onClick={titleAction}
              style={{ borderStyle: 'dashed', borderWidth: 1 }}
            >
              <ArrowBack/>
            </IconButton>
            <span>&nbsp;&nbsp;&nbsp;&nbsp;</span>
            {title}
          </Typography>
        </React.Fragment>
      );
    } else if (title) {
      return title;
    }
    return '';
  };

  // Read at call time, so the Row below keeps one identity across renders.
  const latest = useRef();
  latest.current = { onRowDoubleClick, onRowClick, classes, stateForcedRefresh, setStateForcedRefresh };
  const rowKind = onDoubleClickRowEdit ? 'edit' : onRowDoubleClick ? 'double' : 'plain';

  // The row that was double-clicked is `props.data`, and it always was.
  //
  // This used to read `tableData.id` and use it to index the table's own
  // state, which worked for exactly as long as that field held an array
  // position. Version 8 changed what it holds:
  //
  //     id: row[idSynonym] || index,   // the ROW's id, falling back to index
  //
  // so a mixture numbered 82671 looked itself up at position 82671 of a
  // twenty-five row array, got `undefined`, and every double click in this
  // application threw `Cannot read properties of undefined (reading 'id')`
  // into the console and did nothing. Thirty screens open their detail by
  // double click - a product, a flow, a mixture, a descriptor - so the answer
  // to all of them was a page that did not move.
  //
  // The same renaming had already been seen once, as a selection checkbox
  // whose `value` stopped being an index; this is the same change, in the
  // place nobody looked.
  const Row = useMemo(() => {
    if (rowKind === 'edit') {
      return props => (
        <MTableBodyRow
          onDoubleClick={() => {
            props.data.tableData.editing = 'update';
            latest.current.setStateForcedRefresh(!latest.current.stateForcedRefresh);
          }}
          {...props}
        />
      );
    }
    if (rowKind === 'double') {
      return props => <MTableBodyRow
        {...props}
        onDoubleClick={() => latest.current.onRowDoubleClick(props.data)}
      />;
    }
    return props => <MTableBodyRow
      {...props}
      className={latest.current.onRowClick ? latest.current.classes.rowStyleNotClickable : null}
    />;
  }, [rowKind]);

  const Pagination = useMemo(() => props => (
    <TablePagination
      {...props}
      className={classes.pagination}
      // MUI 4 wrote `1-3 of 3` with a hyphen; MUI 5 changed the default
      // to an en dash. This is version 4's own line, character for
      // character, `more than` included.
      labelDisplayedRows={({ from, to, count }) =>
        `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
      }
    />
  ), [classes.pagination]);

  // Handed to MaterialTable as `components`, and built ONCE. Version 8 remounts
  // a component whose type changes: written inline they were new types on
  // every render, and every save threw the table away - the list jumped back to
  // the top and an open detail panel lost its render.
  const ownComponents = useMemo(() => ({
    // material-table 2 hung its scrollbar rules on a container ABOVE the
    // scrolling element and reached it with a descendant selector:
    //
    //     '& ::-webkit-scrollbar': { '-webkit-appearance': 'none' }
    //
    // Version 8 puts the identical rules on the scrolling element ITSELF,
    // where `& ::-webkit-scrollbar` - a descendant selector - no longer
    // matches its own bar. So the vertical scrollbar stopped being styled,
    // went back to being an overlay, and now fades instead of standing
    // there: the table appears to float where it used to sit against a
    // bar. Same rules, hung one level up again, which is where they were.
    Container: props => (
      <Paper
        elevation={2}
        {...props}
        className={css({
          '& ::-webkit-scrollbar': { WebkitAppearance: 'none' },
          '& ::-webkit-scrollbar:horizontal': { height: 8 },
          '& ::-webkit-scrollbar-thumb': {
            borderRadius: 4,
            border: '2px solid white',
            backgroundColor: 'rgba(0, 0, 0, .3)',
          },
          // Version 2 stacked the sticky header cells at `zIndex: 10`.
          // Version 8 moved it to the `<tr>`, which is not positioned, so it
          // does nothing - and the icon buttons of the rows scrolled under
          // the header, which ARE positioned, painted over it.
          '& thead th': { zIndex: 10 },
          // Version 8 renders a sort-order indicator beside every header,
          // `width: 1em`, whether or not `showColumnSortOrder` is on - and
          // it is off, so the span is empty and still one em wide. Every
          // column that sizes to its content is that much wider, which is
          // why the same data laid out differently: Código MP 110 -> 124,
          // Perigosa 121 -> 135. Version 2 had no such element.
          '& [data-testid="material-table-column-sort-order-indicator"]': {
            display: 'none',
          },
          // Version 8 wraps each header label in the column's drag handle
          // - `position: relative; min-width: 0; display: flex` - and a
          // BLOCK-level box has no line of text around it, so the cell
          // stopped reserving the space a font asks for above and below
          // the baseline. Every table header was 36.50 tall where it had
          // been 37.84, and the handle stretched to the full width of the
          // cell instead of wrapping the words. Version 2 put its label
          // straight into the cell as an `inline-flex`, which is what this
          // restores; `flexDirection: row-reverse` still right-aligns,
          // because the cell's own `text-align` places an inline box.
          // `!important` because the handle carries `display: flex` in
          // its own `style` attribute, and an inline style beats a class.
          '& thead th > div': {
            display: 'inline-flex !important',
            verticalAlign: 'middle',
          },
          // The actions cell. Version 2 put a plain block box between the
          // flex container and the buttons, so the buttons stayed
          // INLINE-level and wrapped like words: a column with four of
          // them could shrink to the width of one and take two rows to
          // draw them. Version 8 makes the buttons flex items, which
          // never wrap, so the column has to be wide enough for all of
          // them and every other column gives up the difference -
          // /products 85 against 55, /flow-steps 160 against 135. A flex
          // container turned back into a block box blockifies nothing,
          // and the buttons are inline-flex again.
          // Scoped to the ACTIONS cell, which material-table always gives
          // `padding: 0px 5px` and the app only ever adds a paddingLeft
          // to. The detail-panel toggle cell has the same shape - a flex
          // div holding icon buttons - and no style attribute, and it was
          // a flex row in version 2 as well: turning THAT one into a
          // block moved its three icons 18px right on every row of every
          // table that has a detail panel.
          '& tbody td[style*="padding: 0px 5px"] > div:has(> button)': {
            display: 'block !important',
          },
        })}
      />
    ),
    Toolbar: props => (
      <div
        style={{ backgroundColor: theme.palette.background.veryLight }}
        // material-table 2 gave its toolbar 8px of padding on the right;
        // 8 takes MUI's default gutter of 24, and the search field and the
        // add button move 16px left on every screen. Scoped to this
        // toolbar: the application's own AppBar wants the 24 and already
        // matches.
        className={css({ '& .MuiToolbar-root': { paddingRight: 8 } })}
      >
        <MTableToolbar {...props} />
      </div>
    ),
  }), []);

  const tableComponents = useMemo(
    () => ({ ...ownComponents, Row, Pagination, ...components }),
    [ownComponents, Row, Pagination, components]
  );

  return (
    <MuiThemeProvider theme={theme}>
      <MaterialTable
        tableRef={ref}
        title={enhancedTitle()}
        icons={tableIcons}
        columns={columns}
        data={orderedData}
        {...forwarded}
        onSelectionChange={onSelectionChange}
        onSearchChange={onSearchChange}
        options={{
          //actionsColumnIndex: -1,
          //tableLayout: 'fixed',
          actionsCellStyle: {
            minWidth: 100,
            paddingLeft: 20
            // alignItems: 'stretch',
            // display:'flex',
            // justifyContent: 'center',
          },
          searchAutoFocus: true,
          debounceInterval: 300,
          searchFieldStyle: onSearchChange && options && options.searchText !== '' ?
            {
              backgroundColor: theme.palette.primary.light,
              borderRadius: '6px 6px 0px 0px'
            } :
            null,
          addRowPosition: 'first',
          detailPanelColumnAlignment: 'right',
          headerStyle: {
            backgroundColor: theme.palette.primary.main,
            //color: theme.palette.secondary.main,
            color: 'white',
            position: 'sticky',
            top: 0
          },
          paging: paging,
          padding: 'dense',
          paginationType: 'normal',
          pageSize: 25,
          pageSizeOptions: [],
          maxBodyHeight: halfSize ? TABLE_HEIGHT_SPLIT(paging, 0) : TABLE_HEIGHT(paging, 0),
          minBodyHeight: halfSize ? TABLE_HEIGHT_SPLIT(paging, 0) : TABLE_HEIGHT(paging, 0),
          ...options
        }}
        components={tableComponents}
        onRowClick={onRowClick}
        editable={editable}
        cellEditable={cellEditable}
        detailPanel={detailPanel}
        localization={
          {
            body: {
              deleteTooltip: 'Apagar',
              editTooltip: 'Editar',
              addTooltip: 'Adicionar'
            },
            toolbar: {
              exportName: 'Exportar CSV',
              exportTitle: 'Exportar CSV'
            },
            header: {
              actions: 'Acções'
            },
            ...localization
          }
        }
      />
    </MuiThemeProvider>
  );
});

export default withStyles(styles)(Component);

Component.propTypes = {
  actions: PropTypes.array,
  cellEditable: PropTypes.object,
  columns: PropTypes.array,
  components: PropTypes.object,
  data: PropTypes.array,
  detailPanel: PropTypes.array,
  editable: PropTypes.object,
  halfSize: PropTypes.bool,
  isLoading: PropTypes.bool,
  localization: PropTypes.object,
  onRowClick: PropTypes.func,
  onRowDoubleClick: PropTypes.func,
  onDoubleClickRowEdit: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  onSearchChange: PropTypes.func,
  options: PropTypes.object,
  order: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  paging: PropTypes.bool,
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  titleAction: PropTypes.func,
  titleBack: PropTypes.bool
};
